import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
} from '@mui/material';
import Receipt from '@mui/icons-material/Receipt';
import Download from '@mui/icons-material/Download';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import ShoppingBag from '@mui/icons-material/ShoppingBag';
import Wallet from '@mui/icons-material/Wallet';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { getPurchasesByUser } from '../../utils/purchases';
import { WalletPassDialog } from '../events/WalletPassDialog';
import { generateSignedAgreementPDF, generateReceiptPDF } from '../../utils/pdfGenerator';

interface PurchasesPageProps {
  onNavigate: (path: string) => void;
}

export const PurchasesPage: React.FC<PurchasesPageProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const { orders, feeCategories } = useData();
  const [purchases, setPurchases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPurchase, setExpandedPurchase] = useState<string | null>(null);
  const [walletPassDialogOpen, setWalletPassDialogOpen] = useState(false);
  const [currentTickets, setCurrentTickets] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [feeCategory, setFeeCategory] = useState('all');

  useEffect(() => {
    const loadPurchases = async () => {
      if (!user?.id) {
        setError('Please log in to view your purchases');
        setLoading(false);
        return;
      }

      try {
        const data = await getPurchasesByUser(user.id, orders, feeCategories);
        setPurchases(data);
      } catch (err) {
        console.error('Failed to load purchases:', err);
        setError('Failed to load purchases');
      } finally {
        setLoading(false);
      }
    };

    loadPurchases();
  }, [user, orders, feeCategories]);

  const handleToggleExpand = (purchaseId: string) => {
    setExpandedPurchase(expandedPurchase === purchaseId ? null : purchaseId);
  };

  const handleViewTickets = (purchase: any) => {
    const tickets = purchase.purchase_items
      .filter((item: any) => item.fee_category === '5')
      .flatMap((item: any) => item.tickets || []);

    if (tickets.length > 0) {
      setCurrentTickets({ tickets, purchaseId: purchase.id });
      setWalletPassDialogOpen(true);
    }
  };

  // Tour-driven view-tickets demo (step 14: Digital Tickets + Wallet).
  // Cursor "clicks" the View Tickets button on the first purchase that
  // has tickets; this trigger opens the WalletPassDialog. Closes when
  // the step actually changes (detected via stepId), not when some
  // other within-step trigger fires.
  useEffect(() => {
    let ownerStepId: string | null = null;
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const stepId: string | undefined = detail?.stepId;
      if (detail?.trigger === 'open-first-ticket-wallet') {
        ownerStepId = stepId ?? null;
        const firstWithTickets = purchases.find((p) =>
          p.purchase_items.some((it: any) => it.fee_category === '5' && (it.tickets?.length ?? 0) > 0),
        );
        if (firstWithTickets) handleViewTickets(firstWithTickets);
      } else if (ownerStepId !== null && stepId !== ownerStepId) {
        ownerStepId = null;
        setWalletPassDialogOpen(false);
        setCurrentTickets(null);
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchases]);

  const handleDownloadAgreement = async (item: any) => {
    if (item.signed_agreements && item.signed_agreements.length > 0) {
      const agreement = item.signed_agreements[0];
      try {
        await generateSignedAgreementPDF(
          item.fee_name,
          agreement.terms_content,
          agreement.signed_by,
          new Date(agreement.signed_at),
          agreement.signature_image
        );
      } catch (error) {
        console.error('Failed to generate PDF:', error);
      }
    }
  };

  const handlePrintReceipt = async (purchase: any) => {
    try {
      await generateReceiptPDF(purchase);
    } catch (error) {
      console.error('Failed to generate receipt:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredPurchases = useMemo(() => {
    return purchases.filter(purchase => {
      const purchaseDate = new Date(purchase.created_at);

      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (purchaseDate < start) return false;
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (purchaseDate > end) return false;
      }

      if (feeCategory !== 'all') {
        const hasCategory = purchase.purchase_items.some(
          (item: any) => item.fee_category === feeCategory
        );
        if (!hasCategory) return false;
      }

      return true;
    });
  }, [purchases, startDate, endDate, feeCategory]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
          color: 'white',
          py: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Receipt sx={{ fontSize: { xs: 40, md: 50 } }} />
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
              }}
            >
              My Purchases
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.95,
              fontWeight: 400,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            }}
          >
            View your order history and download tickets
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {purchases.length > 0 && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Filter Purchases
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  select
                  label="Fee Type"
                  value={feeCategory}
                  onChange={(e) => setFeeCategory(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="5">Event Tickets</MenuItem>
                  <MenuItem value="4">Field Trips</MenuItem>
                  <MenuItem value="2">Bus Pass</MenuItem>
                  <MenuItem value="3">Classroom Supplies</MenuItem>
                  <MenuItem value="1">Donations</MenuItem>
                  <MenuItem value="6">Other</MenuItem>
                </TextField>
              </Box>
            </Box>
            {(startDate || endDate || feeCategory !== 'all') && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    setFeeCategory('all');
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {purchases.length === 0 ? (
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'grey.50',
            }}
          >
            <ShoppingBag sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="text.secondary">
              No purchases yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              When you make a purchase, it will appear here
            </Typography>
            <Button
              variant="contained"
              onClick={() => onNavigate('/events')}
            >
              Browse Events
            </Button>
          </Paper>
        ) : filteredPurchases.length === 0 ? (
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'grey.50',
            }}
          >
            <ShoppingBag sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="text.secondary">
              No purchases match your filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try adjusting your filter criteria
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setFeeCategory('all');
              }}
            >
              Clear Filters
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} data-tour="purchase-ticket">
            {(() => {
              const firstTicketPurchaseId = filteredPurchases.find((p) =>
                p.purchase_items.some(
                  (it: any) => it.fee_category === '5' && (it.tickets?.length ?? 0) > 0,
                ),
              )?.id;
              return filteredPurchases.map((purchase) => {
              const hasTickets = purchase.purchase_items.some(
                (item: any) => item.fee_category === '5'
              );
              const isExpanded = expandedPurchase === purchase.id;
              const isTourTarget = purchase.id === firstTicketPurchaseId;

              return (
                <Card
                  key={purchase.id}
                  sx={{ overflow: 'visible' }}
                  {...(isTourTarget ? { 'data-tour': 'purchase-ticket-card' } : {})}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        mb: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Typography variant="h6" fontWeight={600}>
                            Order #{purchase.confirmation_number}
                          </Typography>
                          <Chip
                            label={purchase.status}
                            color="success"
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(purchase.created_at)}
                        </Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={600} color="primary">
                        ${purchase.total.toFixed(2)}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleToggleExpand(purchase.id)}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {purchase.purchase_items.length} item
                        {purchase.purchase_items.length > 1 ? 's' : ''}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      >
                        <ExpandMore />
                      </IconButton>
                    </Box>

                    <Collapse in={isExpanded}>
                      <Box sx={{ mt: 2 }}>
                        {purchase.purchase_items.map((item: any) => (
                          <Box
                            key={item.id}
                            sx={{
                              p: 2,
                              mb: 2,
                              bgcolor: 'grey.50',
                              borderRadius: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 1,
                              }}
                            >
                              <Typography variant="body1" fontWeight={600}>
                                {item.fee_name}
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                ${item.total_price.toFixed(2)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {item.fee_category === '5' && item.questionnaire_responses?.ticketType ? (
                                <>
                                  {item.quantity} x ${item.unit_price.toFixed(2)} ({item.questionnaire_responses.ticketType})
                                </>
                              ) : (
                                <>
                                  {item.quantity} x ${item.unit_price.toFixed(2)}
                                </>
                              )}
                            </Typography>

                            {item.partial_payment_amount && item.partial_payment_amount < item.total_price && (
                              <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.lighter', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                                <Typography variant="caption" fontWeight={600} color="warning.dark">
                                  Partial Payment
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Paid: ${item.partial_payment_amount.toFixed(2)} of ${item.total_price.toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="error.main">
                                  Balance Due: ${(item.total_price - item.partial_payment_amount).toFixed(2)}
                                </Typography>
                              </Box>
                            )}

                            {item.questionnaire_responses && Object.keys(item.questionnaire_responses).length > 0 && (() => {
                              const filteredResponses = Object.entries(item.questionnaire_responses).filter(
                                ([key]) => !(item.fee_category === '5' && key === 'ticketType')
                              );
                              return filteredResponses.length > 0 ? (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
                                  <Typography variant="caption" fontWeight={600} color="info.dark" gutterBottom>
                                    Questionnaire Responses
                                  </Typography>
                                  {filteredResponses.map(([key, value]: [string, any]) => (
                                    <Typography key={key} variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                      <strong>{key}:</strong> {Array.isArray(value) ? value.join(', ') : value}
                                    </Typography>
                                  ))}
                                </Box>
                              ) : null;
                            })()}

                            {item.signed_agreements && item.signed_agreements.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Download />}
                                  onClick={() => handleDownloadAgreement(item)}
                                >
                                  Download Signed Agreement
                                </Button>
                              </Box>
                            )}

                            {item.fee_category === '5' &&
                              item.tickets &&
                              item.tickets.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Chip
                                    icon={<ConfirmationNumber />}
                                    label={`${item.tickets.length} ticket${
                                      item.tickets.length > 1 ? 's' : ''
                                    }`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                              )}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        startIcon={<Receipt />}
                        onClick={() => handlePrintReceipt(purchase)}
                      >
                        Print Receipt
                      </Button>
                      {hasTickets && (
                        <Button
                          variant="contained"
                          startIcon={<Wallet />}
                          onClick={() => handleViewTickets(purchase)}
                          {...(isTourTarget ? { 'data-tour': 'purchase-view-tickets-btn' } : {})}
                        >
                          View Tickets
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
              });
            })()}
          </Box>
        )}
      </Container>

      {currentTickets && (
        <WalletPassDialog
          open={walletPassDialogOpen}
          onClose={() => {
            setWalletPassDialogOpen(false);
            setCurrentTickets(null);
          }}
          passData={currentTickets}
        />
      )}
    </>
  );
};
